import type React from 'react';
import { useEffect, useState } from 'react';
import 'dayjs/locale/de-at';
import './styles/fonts/inter/400.css';
import './styles/fonts/inter/500.css';
import './styles/fonts/inter/600.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
import { ApolloProvider } from '@apollo/client/react';
import { App as AntApp, Button, ConfigProvider, Result, Spin } from 'antd';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v8';
import { BrowserRouter } from 'react-router';
import { apolloFactory } from './graphql/ApolloFactory';
import 'antd/dist/reset.css';
import 'react-quill-new/dist/quill.snow.css';
import './styles/quill-overrides.css';
import './styles/global.css';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { AuthLoadingScreen } from '@medcom/shared-ui/components/AuthLoadingScreen.tsx';
import { SessionExpired } from '@medcom/shared-ui/components/SessionExpired.tsx';
import { currentUrlWithoutAuthParams, isSessionExpiredPage } from '@medcom/shared-ui/utils/oidc.ts';
import { reportError, reportRenderError, setTelemetryUser } from '@medcom/shared-ui/utils/telemetry.ts';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import { ErrorBoundary } from 'react-error-boundary';
import { useAuth } from 'react-oidc-context';
import { ASSIGNMENT_FOR_CURRENT_USER_QUERY, USER_DOCTOR_AUTH_CHECK_QUERY } from './App.gql';
import { AppPage } from './AppPage.tsx';
import { AisLaunchFailed } from './ais/AisLaunch.tsx';
import { AisLinkListener } from './ais/AisLinkListener.tsx';
import { AisLinkRedeem } from './ais/AisLinkRedeem.tsx';
import { aisLaunchError, isAisLinkPage, isAisSessionExpired, isAisSignedOutPage, sessionEndedPath } from './ais/aisSignin.ts';
import { useAisIdentity } from './ais/useAisIdentity.ts';
import { useAisTabName } from './ais/useAisTabName.ts';
import { AppStateResetter } from './components/AppStateResetter';
import { AppVersionAutoUpdater } from './components/AppVersionAutoUpdater.tsx';
import { AppVersionCheckerModal } from './components/AppVersionCheckerModal';
import { ErrorFallback } from './components/ErrorFallback';
import { ImportFileWatcher } from './components/ImportFileWatcher';
import { OfflineBlockingModal } from './components/OfflineBlockingModal';
import { SignedOut } from './components/SignedOut';
import { DynamicConfigProvider } from './DynamicConfigProvider.tsx';
import { useCurrentContextStore } from './hooks/store/useCurrentContextStore.ts';
import { defaultTheme, useThemeStore } from './hooks/store/useThemeStore.ts';
import { userSettingsStoreDefaultName, useUserSettingsStore } from './hooks/store/useUserSettingsStore.ts';
import { useAuthEvents } from './hooks/useAuthEvents';
import { DOCTOR_THEME_QUERY } from './hooks/useDoctorTheme.gql';
import { ParamCompilationTracker } from './ParamCompilationTracker';
import { PatientImportModal } from './PatientImportModal';
import { SocketProvider } from './socket/SocketProvider';
import { hasAnyRelevantRole } from './utils/user';
import { NewsConfirmationModal } from './views/shared/news/NewsConfirmationModal.tsx';

dayjs.locale('de-at');
// plugins needed for production build
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

Spin.setDefaultIndicator(<LoadingOutlined />);

const { apolloClient } = apolloFactory();

const App: React.FC = () => {
  const auth = useAuth();
  const [appInitialized, setAppInitialized] = useState(false);
  const [initError, setInitError] = useState(false);
  const { userId, setUserId, currentDoctorId, setCurrentDoctorId, setPrimaryDoctorId } = useCurrentContextStore();
  const { setTheme } = useThemeStore();
  const aisIdentity = useAisIdentity();

  useEffect(() => {
    // reset desktop user info
    window.nativeApi?.setUserInfo(null);
  }, []);

  useEffect(() => {
    if (auth.error) {
      reportError(auth.error, { source: 'oidc', url: currentUrlWithoutAuthParams() });
    }
  }, [auth.error]);

  useEffect(() => {
    if (!auth.isAuthenticated || appInitialized) {
      return;
    }

    setTelemetryUser({
      id: auth.user?.profile.sub,
      username: auth.user?.profile.preferred_username,
      email: auth.user?.profile.email,
    });

    const initialize = async () => {
      try {
        const { data, error } = await apolloClient.query({
          query: ASSIGNMENT_FOR_CURRENT_USER_QUERY,
        });
        const primaryDoctorId = data?.assignmentForCurrentUser?.primaryDoctor?.id ?? '';

        if (error) {
          setInitError(true);
        } else {
          let doctorId = '';
          if (
            currentDoctorId &&
            userId === auth.user?.profile.sub // from sessionStorage
          ) {
            const { data: result } = await apolloClient.query({
              query: USER_DOCTOR_AUTH_CHECK_QUERY,
              variables: { doctorId: currentDoctorId },
            });
            if (result?.isCurrentUserAuthorizedForDoctor) {
              doctorId = currentDoctorId;
            } else {
              doctorId = primaryDoctorId;
            }
          } else {
            doctorId = primaryDoctorId;
          }

          setPrimaryDoctorId(primaryDoctorId);
          setCurrentDoctorId(doctorId);
          setUserId(auth.user?.profile.sub ?? '');

          useUserSettingsStore.persist.setOptions({
            name: `${userSettingsStoreDefaultName}_${auth.user?.profile.sub}`,
          });
          useUserSettingsStore.persist.rehydrate();
          localStorage.removeItem(userSettingsStoreDefaultName);

          // now load theme if doctor and role is available
          if (doctorId && hasAnyRelevantRole(auth.user)) {
            const { data } = await apolloClient.query({
              query: DOCTOR_THEME_QUERY,
              variables: {
                doctorId: doctorId,
              },
            });
            if (data?.doctor?.theme) {
              setTheme(data.doctor.theme, false);
            }
          }
        }
      } catch {
        setInitError(true);
      } finally {
        setAppInitialized(true);
      }
    };
    initialize();
  }, [auth, userId, setUserId, currentDoctorId, setCurrentDoctorId, setPrimaryDoctorId, setTheme, appInitialized]);

  // while useAuthEvents retries a failed callback, auth.error is still set — don't
  // flash the error screen before the redirect kicks in
  if (!appInitialized && auth.error && !auth.activeNavigator) {
    return (
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: defaultTheme.colorPrimary,
          },
        }}
      >
        <Result
          status={500}
          title="Authentifizierung fehlgeschlagen"
          subTitle={auth.error.message}
          extra={
            <Button icon={<ReloadOutlined />} onClick={() => (window.location.href = '/')} type="primary">
              Applikation neu laden
            </Button>
          }
        />
      </ConfigProvider>
    );
  }

  // Not authenticated: either the auto sign-in redirect is in flight, or it got
  // stuck (iOS standalone PWA opens Keycloak in a separate in-app browser sheet, so
  // the webview never navigates). Show the loading/retry screen, not the main app
  // tree — otherwise the plain LoadingIndicator below would trap the user with no
  // way back to the Keycloak dialog.
  if (auth.isLoading || !auth.isAuthenticated) {
    return (
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: defaultTheme.colorPrimary,
          },
        }}
      >
        <AuthLoadingScreen />
      </ConfigProvider>
    );
  }

  return (
    <ApolloProvider client={apolloClient}>
      <DynamicConfigProvider>
        <AntApp>
          <ErrorBoundary FallbackComponent={ErrorFallback} onError={reportRenderError}>
            {!appInitialized && <AuthLoadingScreen signinRetry={false} />}
            {appInitialized && initError && (
              <Result
                status={500}
                title="Beim Laden der Applikation ist ein Fehler aufgetreten"
                subTitle="Bitte versuchen Sie die Applikation neu zu laden."
                extra={
                  <Button icon={<ReloadOutlined />} onClick={() => (window.location.href = '/')} type="primary">
                    Applikation neu laden
                  </Button>
                }
              />
            )}
            {appInitialized && !initError && (
              <SocketProvider>
                <BrowserRouter>
                  <NuqsAdapter>
                    <AppPage />
                    <AppStateResetter />
                    <AppVersionCheckerModal />
                    <AppVersionAutoUpdater />
                    <ImportFileWatcher />
                    <PatientImportModal />
                    {aisIdentity && <AisLinkListener />}
                    <OfflineBlockingModal />
                    <NewsConfirmationModal />
                    <ParamCompilationTracker />
                  </NuqsAdapter>
                </BrowserRouter>
              </SocketProvider>
            )}
          </ErrorBoundary>
        </AntApp>
      </DynamicConfigProvider>
    </ApolloProvider>
  );
};

// None of these pages signs in by itself: the user decides, and for an AIS user it would end on the medcom login form
export const AppRoot: React.FC = () => {
  const signedOut = isAisSignedOutPage();
  const sessionExpired = isSessionExpiredPage();
  const linkPage = isAisLinkPage();
  const launchError = aisLaunchError();
  useAuthEvents({
    autoSignin: !signedOut && !sessionExpired && !linkPage && launchError === null,
    sessionEndedPath,
  });
  useAisTabName();

  if (linkPage) {
    return (
      <ConfigProvider theme={{ token: { colorPrimary: defaultTheme.colorPrimary } }}>
        <AisLinkRedeem />
      </ConfigProvider>
    );
  }

  if (launchError) {
    return (
      <ConfigProvider theme={{ token: { colorPrimary: defaultTheme.colorPrimary } }}>
        <AisLaunchFailed reason={launchError} />
      </ConfigProvider>
    );
  }

  return signedOut || sessionExpired ? (
    <ConfigProvider theme={{ token: { colorPrimary: defaultTheme.colorPrimary } }}>
      {signedOut ? <SignedOut expired={isAisSessionExpired()} /> : <SessionExpired />}
    </ConfigProvider>
  ) : (
    <App />
  );
};
